import {
  getAllControlPeriods,
  getFoodControlPointInstancesFromLocationId,
  getFoodControlPointsFromLocationId,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export function useFoodReports(locationId: string) {
  const foodControlPointsQuery = useQuery({
    queryKey: ["foodControlPoints", locationId],
    queryFn: () => getFoodControlPointsFromLocationId(locationId),
    enabled: !!locationId,
  });

  const { data: allPeriods, isPending: isControlPeriodsPending } = useQuery({
    queryFn: () => getAllControlPeriods(),
    queryKey: ["allControlPeriods"],
  });

  const foodControlPointInstancesQuery = useQuery({
    queryKey: ["foodControlPointInstances", locationId],
    queryFn: () => getFoodControlPointInstancesFromLocationId(locationId),
    enabled: !!locationId,
  });

  const isPending =
    foodControlPointsQuery.isPending ||
    foodControlPointInstancesQuery.isPending ||
    isControlPeriodsPending;

  const error =
    foodControlPointsQuery.error || foodControlPointInstancesQuery.error;

  const foodReports = useMemo(() => {
    if (
      !foodControlPointsQuery.data ||
      !foodControlPointInstancesQuery.data ||
      isPending
    ) {
      return [];
    }

    const foodControlPoints = foodControlPointsQuery.data.filter(
      (c) => !c.isDeleted,
    );
    const foodControlPointInstances =
      foodControlPointInstancesQuery.data.filter((c) => !c.isDeleted);

    return foodControlPoints.map((point) => {
      const relatedInstances = foodControlPointInstances.filter(
        (instance) => instance.foodControlPointId === point.id,
      );

      return {
        id: point.id,
        name: point.name,
        type: point.type,
        controlPeriod: allPeriods?.find(
          (period) => period.id === point.controlPeriodId,
        ),
        foodControlPointInstances: relatedInstances,
      };
    });
  }, [
    foodControlPointsQuery.data,
    foodControlPointInstancesQuery.data,
    allPeriods,
    isPending,
  ]);

  return { foodReports: foodReports, isPending, error };
}
